'use client';

import { LineItem, Order } from '@commercelayer/sdk';
import { CartQuery, SiteLocale } from '@graphql/generated';
import { AppCommerceLayerClient } from '@lib/AppCommerceLayerClient';
import { useAppContext } from '@lib/context/appContext';
import t, { Labels, tt } from '@lib/locales';
import { getDateRange, getMaxDeliveryDays } from '@lib/utils/clUtils';
import { trackStepBeginCheckoutEvent, trackViewCartEvent } from '@lib/utils/gtmUtils';
import { checkAndUpdateAvailabilities } from '@utils/cart-utils';
import { getShippingMessage } from '@utils/shipping-message';
import { useRouter } from 'next/navigation';
import { ActionDispatch, AnyActionArg, useEffect, useState } from 'react';
import config from '../../../data/config.json';
import { Loading } from '../layout/Loading';
import Banner from './Banner';
import CartLineItem from './CartLineItem';
import { Coupon } from './Coupon';

type Props = {
  locale: SiteLocale;
  checkoutUrl: string;
  banner: CartQuery['cart'];
};
const Cart = ({ locale, checkoutUrl, banner = null }: Props) => {
  const router = useRouter();
  const { client, dispatch } = useAppContext() as {
    client: AppCommerceLayerClient;
    dispatch: ActionDispatch<AnyActionArg>;
  };
  const { availabilityShowThreshold: qtTreshold, maxPromptDeliveryHours } = config.configuration;
  const daysLimit = Math.round(maxPromptDeliveryHours / 24);

  const [order, setOrder] = useState<Order>(null);
  const [loading, setLoading] = useState(true);
  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [initGtm, setInitGtm] = useState(false);
  const [message, setMessage] = useState(null);

  const handleUpdateCartItemCount = (order: Order) => {
    const cartItems = client.countOrderProductsQuantity(order);
    dispatch({ type: 'SET_NUM_ITEMS', data: cartItems });
    /**
     * If the cart is empty and is associated with a customer, delete it
     * to consent to the user to change the email address in the checkout
     * if not logged in
     */
    if (cartItems == 0 && order.customer_email) {
      client.deleteCart();
    }
  };

  const getData = async () => {
    setLoading(true);
    try {
      const orderObj = await client.getOrder();
      if (orderObj) {
        setOrder(orderObj);
        setLineItems(orderObj.line_items);

        handleUpdateCartItemCount(orderObj);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (client) {
      getData();
    }
  }, [client]);

  useEffect(() => {
    if (!initGtm && order) {
      setInitGtm(true);
      trackViewCartEvent(order);

      const checkQuantity = async () => {
        const orderIsChanged = await checkAndUpdateAvailabilities(client, lineItems);
        if (orderIsChanged) {
          await displayQuantityAndToast();
        }
      };

      checkQuantity();
    }
  }, [order, initGtm, dispatch]);

  useEffect(() => {
    async function fetchMessage() {
      const message = await getShippingMessage(locale);
      setMessage(message);
    }
    fetchMessage();
  }, []);

  async function displayQuantityAndToast() {
    dispatch({
      type: 'SHOW_TOAST',
      data: { message: t(locale, 'check_cart'), error: true, autoClose: false },
    });
    await getData();
  }

  const refreshData = async (): Promise<Order> => {
    const orderObj = await client.checkEtagUpdate(order.metadata?.etag);
    if (!orderObj) {
      const errorTitle = t(locale, 'error_boundary_title');
      const errorSubtitle = t(locale, 'error_boundary_subtitle');
      dispatch({
        type: 'SHOW_TOAST',
        data: { message: `${errorTitle} ${errorSubtitle}`, error: true, autoClose: false },
      });
      router.push('/');
      throw new Error('etag not updated');
    }
    setOrder(orderObj);
    setLineItems(orderObj.line_items);
    return orderObj;
  };

  const updateQuantity = async (lineItem: LineItem, quantity: number) => {
    setLoading(true);
    try {
      await client.updateLineItemQuantity(lineItem, quantity);
      const order = await refreshData();
      handleUpdateCartItemCount(order);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const removeLineItem = async (lineItem: LineItem) => {
    setLoading(true);
    try {
      await client.removeLineItem(lineItem);
      const order = await refreshData();
      handleUpdateCartItemCount(order);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const types = ['skus', 'bundles'];
  const products = lineItems?.filter((l) => types.includes(l.item_type));

  if (!lineItems || lineItems.length === 0 || products?.length === 0) {
    let message: Labels = 'cart_empty';
    if (loading) {
      message = 'loading';
    }
    return (
      <div className="cart">
        <div className="cart_message">{t(locale, message)}</div>
      </div>
    );
  }

  const info = lineItems.reduce(
    (obj, i) => {
      const { quantity, total_amount_cents: totalAmountCents } = i;
      const amount = totalAmountCents;
      obj.total += amount / 100;
      if (types.includes(i.item_type)) {
        obj.num += quantity;
      }
      if (i.metadata?.levels) {
        const { minDay, maxDay } = getMaxDeliveryDays(i.metadata?.levels);
        obj.maxDays = Math.max(maxDay, obj.maxDays);
        obj.minDays = Math.max(minDay, obj.minDays);
      }
      return obj;
    },
    { total: 0, num: 0, maxDays: 0, minDays: 0 }
  );

  const handleClick = async (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setLoading(true);

    const orderIsChanged = await checkAndUpdateAvailabilities(client, lineItems);
    if (orderIsChanged) {
      await displayQuantityAndToast();
    } else {
      trackStepBeginCheckoutEvent(order);
      router.push(checkoutUrl);
    }
    setLoading(false);
  };

  return (
    <div className="cart-container">
      {loading && <Loading locale={locale} />}
      <div className="cart-body">
        <div className="cart_line-items">
          {products.map((lineItem) => (
            <CartLineItem
              daysLimit={daysLimit}
              qtTreshold={qtTreshold}
              key={lineItem.id}
              locale={locale}
              lineItem={lineItem}
              handleUpdateQuantity={updateQuantity}
              handleRemoveLineItem={removeLineItem}
              loading={loading}
            />
          ))}
        </div>

        {order.id && products.length > 0 && (
          <div className="cart-section_wrap">
            <Banner data={banner} />
            <div className="coupon__wrap">
              <Coupon
                orderId={order.id}
                onSuccess={() => getData()}
                locale={locale}
                couponCode={order.coupon_code}
              />
            </div>

            <div className="cart_summary">
              {info?.maxDays > 0 && (
                <div className="cart_summary__delivery">
                  {!!message ? (
                    <span>{message}</span>
                  ) : info.maxDays > daysLimit ? (
                    <span>
                      {t(locale, 'delivery_date')}{' '}
                      {getDateRange(locale, info.minDays, info.maxDays)}
                    </span>
                  ) : (
                    <span>{tt(locale, 'delivery_times', { days: info.maxDays.toString() })}</span>
                  )}
                </div>
              )}
              <div className="cart_summary__subtotal">
                <p>{t(locale, 'num')}</p>
                <span>{info?.num}</span>
              </div>
              <div className="cart_summary__spacer"></div>
              <div className="cart_summary__subtotal">
                <p>
                  {t(locale, 'subtotal')}
                  {products.find(
                    (l) =>
                      !!l.options_amount_float ||
                      (!!l?.metadata?.excise_duties && l?.metadata?.excise_duties !== 0)
                  )
                    ? ` (${t(locale, 'fees_included')})`
                    : ''}
                </p>
                <span>{order.formatted_subtotal_amount}</span>
              </div>
              {order.discount_amount_cents !== 0 && (
                <div className="cart_summary__subtotal">
                  <p>{t(locale, 'discount')}</p>
                  <span>{order.formatted_discount_amount}</span>
                </div>
              )}
              {lineItems
                .filter((i) => i.item_type === 'adjustments')
                .map((lineItem) => (
                  <div className="cart_summary__subtotal" key={lineItem.id}>
                    <p>{t(locale, lineItem.name as Labels)}</p>
                    <span>
                      {lineItem.name == 'shipping_cost' && lineItem.total_amount_float == 0
                        ? t(locale, 'free_shipping')
                        : lineItem.formatted_total_amount}
                    </span>
                  </div>
                ))}
              <div className="cart_summary__total">
                <p>{t(locale, 'total')}</p>
                <div>{order.formatted_total_amount_with_taxes}</div>
              </div>
              <a
                role="button"
                href={checkoutUrl}
                className="button-checkout"
                onClick={handleClick}
                aria-disabled={loading}
              >
                {t(locale, 'checkout')}
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cart;
